import React from "react"
import {
    Outlet,
    useNavigate,
    useLocation
} from "react-router-dom"
import {
    Box,
    Drawer,
    IconButton,
    Tooltip,
    Typography,
    Avatar,
    Button,
    useTheme,
    useMediaQuery
} from "@mui/material"
import {
    MenuOutlined,
    ChevronLeft,
    ChevronRight,
    ArrowBack,
    NavigateNext
} from "@mui/icons-material"
import _ from "lodash"

const SIDEBAR_WIDTH = 272
const SIDEBAR_COLLAPSED_WIDTH = 76

function getCurrentUser() {
    try {
        const user = localStorage.getItem("current_user")
        return user ? _.get(JSON.parse(user), "user_data", null) : null
    } catch (error) {
        return null
    }
}

function readCollapsed(storageKey) {
    try {
        return localStorage.getItem(storageKey) === "true"
    } catch (error) {
        return false
    }
}

/**
 * Sidebar + page header layout shared by the Admin panel and Settings.
 *
 * sections: [{ title, items: [{ name, description, to, icon }] }]
 * Item `to` values are child route segments of `basePath`.
 */
export default function SectionLayout({
    basePath,
    title,
    subtitle,
    icon,
    sections,
    collapseStorageKey
}) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const navigate = useNavigate()
    const location = useLocation()

    const user = React.useMemo(getCurrentUser, [])
    const navItems = React.useMemo(
        () => sections.flatMap((section) => section.items),
        [sections]
    )

    const [mobileOpen, setMobileOpen] = React.useState(false)
    const [collapsed, setCollapsed] = React.useState(() => readCollapsed(collapseStorageKey))

    const activeSegment = location.pathname.startsWith(basePath)
        ? location.pathname.slice(basePath.length).split("/")[1] || ""
        : ""
    const activeItem = navItems.find((item) => item.to === activeSegment)

    React.useEffect(() => {
        if (!activeSegment && navItems.length > 0) {
            navigate(`${basePath}/${navItems[0].to}`, { replace: true })
        }
    }, [activeSegment, navItems, basePath, navigate])

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            try {
                localStorage.setItem(collapseStorageKey, String(!prev))
            } catch (error) {
                // Preference is a convenience only
            }
            return !prev
        })
    }

    const handleNav = (to) => {
        navigate(`${basePath}/${to}`)
        setMobileOpen(false)
    }

    const isRail = collapsed && !isMobile
    const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "User"
    const initials = fullName
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

    const sidebarContent = (
        <Box className={`section-sidebar ${isRail ? "is-rail" : ""}`}>

            {/* ================= HEADER ================= */}

            <Box className="section-sidebar-header">
                <Box className="section-brand-icon">
                    {icon}
                </Box>
                {!isRail && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography className="section-brand-title">
                            {title}
                        </Typography>
                        <Typography className="section-brand-subtitle">
                            {subtitle}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ================= NAV ================= */}

            <Box component="nav" className="section-nav" aria-label={`${title} navigation`}>
                {sections.map((section) => (
                    <Box key={section.title} className="section-nav-section">
                        {isRail ? (
                            <Box className="section-nav-divider" />
                        ) : (
                            <Typography className="section-nav-section-title">
                                {section.title}
                            </Typography>
                        )}

                        {section.items.map((item) => {
                            const isActive = item.to === activeSegment
                            const button = (
                                <Box
                                    component="button"
                                    type="button"
                                    key={item.to}
                                    onClick={() => handleNav(item.to)}
                                    className={`section-nav-item ${isActive ? "is-active" : ""}`}
                                    aria-current={isActive ? "page" : undefined}
                                >
                                    <Box className="section-nav-icon">
                                        {item.icon}
                                    </Box>
                                    {!isRail && (
                                        <Box className="section-nav-text">
                                            <span className="section-nav-label">
                                                {item.name}
                                            </span>
                                            <span className="section-nav-description">
                                                {item.description}
                                            </span>
                                        </Box>
                                    )}
                                </Box>
                            )

                            return isRail ? (
                                <Tooltip key={item.to} title={item.name} placement="right" arrow>
                                    {button}
                                </Tooltip>
                            ) : button
                        })}
                    </Box>
                ))}
            </Box>

            {/* ================= FOOTER ================= */}

            <Box className="section-sidebar-footer">
                <Box className="section-user-card">
                    <Avatar className="section-user-avatar">{initials}</Avatar>
                    {!isRail && (
                        <Box sx={{ minWidth: 0 }}>
                            <Typography className="section-user-name" noWrap>
                                {fullName}
                            </Typography>
                            <Typography className="section-user-email" noWrap>
                                {user?.email}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {isRail ? (
                    <Tooltip title="Back to dashboard" placement="right" arrow>
                        <IconButton
                            className="section-back-icon"
                            onClick={() => navigate("/")}
                            aria-label="Back to dashboard"
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button
                        fullWidth
                        startIcon={<ArrowBack fontSize="small" />}
                        onClick={() => navigate("/")}
                        className="section-back-button"
                    >
                        Back to dashboard
                    </Button>
                )}

                {!isMobile && (
                    <IconButton
                        onClick={toggleCollapsed}
                        className="section-collapse-button"
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
                    </IconButton>
                )}
            </Box>
        </Box>
    )

    return (
        <Box className="section-layout">

            {/* ================= SIDEBAR ================= */}

            {isMobile ? (
                <Drawer
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: "none" } }}
                >
                    {sidebarContent}
                </Drawer>
            ) : (
                <Box
                    component="aside"
                    className="section-sidebar-shell"
                    sx={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
                >
                    {sidebarContent}
                </Box>
            )}

            {/* ================= MAIN ================= */}

            <Box component="section" className="section-main">
                <Box className="section-page-header">
                    {isMobile && (
                        <IconButton
                            onClick={() => setMobileOpen(true)}
                            className="section-menu-button"
                            aria-label={`Open ${title} menu`}
                        >
                            <MenuOutlined />
                        </IconButton>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Box className="section-breadcrumb">
                            <span>{title}</span>
                            <NavigateNext fontSize="inherit" />
                            <span className="section-breadcrumb-current">
                                {activeItem?.name || "Overview"}
                            </span>
                        </Box>
                        <Typography component="h1" className="section-page-title">
                            {activeItem?.name || title}
                        </Typography>
                        {activeItem?.description && (
                            <Typography className="section-page-description">
                                {activeItem.description}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box className="section-content">
                    <Outlet />
                </Box>
            </Box>

            <style>{`
                .section-layout {
                    display: flex;
                    align-items: flex-start;
                    width: 100%;
                    min-height: calc(100vh - 83px);
                    background: var(--bg-main);
                    font-family: var(--font-primary);
                }

                /* =====================================
                   SIDEBAR
                ===================================== */

                .section-sidebar-shell {
                    position: sticky;
                    top: 83px;
                    height: calc(100vh - 83px);
                    flex-shrink: 0;
                    border-right: 1px solid var(--border-color);
                    background: var(--bg-card);
                    transition: width .2s ease;
                    overflow: hidden;
                }

                .section-sidebar {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    background: var(--bg-card);
                }

                .section-sidebar-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 22px 20px 18px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .section-sidebar.is-rail .section-sidebar-header {
                    justify-content: center;
                    padding: 22px 0 18px;
                }

                .section-brand-icon {
                    width: 40px;
                    height: 40px;
                    flex-shrink: 0;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ffffff;
                    background: linear-gradient(135deg, #6366f1, #4f46e5);
                    box-shadow: 0 6px 16px rgba(79, 70, 229, .25);
                }

                .section-brand-title {
                    font-family: var(--font-heading) !important;
                    font-size: 16px !important;
                    font-weight: 700 !important;
                    color: var(--text-primary);
                    line-height: 1.2 !important;
                }

                .section-brand-subtitle {
                    font-size: 12px !important;
                    color: var(--text-light);
                }

                .section-nav {
                    flex: 1;
                    overflow-y: auto;
                    padding: 8px 12px 16px;
                }

                .section-nav-section {
                    margin-top: 14px;
                }

                .section-nav-section-title {
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    letter-spacing: .08em;
                    text-transform: uppercase;
                    color: var(--text-light);
                    padding: 0 10px 8px;
                }

                .section-nav-divider {
                    height: 1px;
                    margin: 0 10px 10px;
                    background: #f1f5f9;
                }

                .section-nav-item {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px;
                    margin-bottom: 4px;
                    border: none;
                    border-radius: 12px;
                    background: transparent;
                    color: var(--text-secondary);
                    text-align: left;
                    cursor: pointer;
                    font-family: inherit;
                    transition: background .15s ease, color .15s ease;
                }

                .section-sidebar.is-rail .section-nav-item {
                    justify-content: center;
                }

                .section-nav-item:hover {
                    background: rgba(79, 70, 229, .05);
                    color: var(--color-primary);
                }

                .section-nav-item:focus-visible {
                    outline: 2px solid var(--color-primary);
                    outline-offset: 2px;
                }

                .section-nav-item.is-active {
                    background: var(--color-primary-light);
                    color: var(--color-primary);
                }

                .section-nav-icon {
                    width: 36px;
                    height: 36px;
                    flex-shrink: 0;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    color: var(--text-light);
                    transition: all .15s ease;
                }

                .section-nav-icon svg {
                    font-size: 20px;
                }

                .section-nav-item:hover .section-nav-icon {
                    color: var(--color-primary);
                }

                .section-nav-item.is-active .section-nav-icon {
                    background: var(--color-primary);
                    color: #ffffff;
                    box-shadow: 0 4px 12px rgba(79, 70, 229, .3);
                }

                .section-nav-text {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }

                .section-nav-label {
                    font-size: 14px;
                    font-weight: 600;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .section-nav-description {
                    font-size: 12px;
                    color: var(--text-light);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .section-sidebar-footer {
                    padding: 14px 12px;
                    border-top: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    gap: 10px;
                }

                .section-sidebar.is-rail .section-sidebar-footer {
                    align-items: center;
                }

                .section-user-card {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px;
                    border-radius: 12px;
                    background: #f8fafc;
                }

                .section-sidebar.is-rail .section-user-card {
                    padding: 0;
                    background: transparent;
                }

                .section-user-avatar {
                    width: 36px !important;
                    height: 36px !important;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    background: #e0e7ff !important;
                    color: var(--color-primary) !important;
                }

                .section-user-name {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: var(--text-primary);
                }

                .section-user-email {
                    font-size: 11px !important;
                    color: var(--text-light);
                }

                .section-back-button {
                    justify-content: flex-start !important;
                    text-transform: none !important;
                    font-family: inherit !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    color: var(--text-secondary) !important;
                    border-radius: 10px !important;
                    padding: 8px 12px !important;
                }

                .section-back-button:hover,
                .section-back-icon:hover {
                    background: rgba(79, 70, 229, .05) !important;
                    color: var(--color-primary) !important;
                }

                .section-back-icon {
                    color: var(--text-secondary) !important;
                }

                .section-collapse-button {
                    align-self: flex-end;
                    width: 32px;
                    height: 32px;
                    border: 1px solid var(--border-color) !important;
                    color: var(--text-secondary) !important;
                }

                .section-sidebar.is-rail .section-collapse-button {
                    align-self: center;
                }

                /* =====================================
                   MAIN
                ===================================== */

                .section-main {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                }

                .section-page-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 24px 32px 20px;
                    background: var(--bg-card);
                    border-bottom: 1px solid var(--border-color);
                }

                .section-menu-button {
                    border: 1px solid var(--border-color) !important;
                    border-radius: 10px !important;
                    color: var(--text-secondary) !important;
                }

                .section-breadcrumb {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    color: var(--text-light);
                    margin-bottom: 4px;
                }

                .section-breadcrumb-current {
                    color: var(--color-primary);
                    font-weight: 600;
                }

                .section-page-title {
                    font-family: var(--font-heading) !important;
                    font-size: 24px !important;
                    font-weight: 700 !important;
                    color: var(--text-primary);
                    letter-spacing: -.3px;
                    line-height: 1.2 !important;
                }

                .section-page-description {
                    font-size: 13px !important;
                    color: var(--text-secondary);
                    margin-top: 2px !important;
                }

                .section-content {
                    flex: 1;
                    padding: 24px 32px 40px;
                }

                @media (max-width: 899px) {
                    .section-layout {
                        min-height: calc(100vh - 71px);
                    }

                    .section-page-header {
                        padding: 16px;
                    }

                    .section-page-title {
                        font-size: 20px !important;
                    }

                    .section-content {
                        padding: 16px;
                    }
                }
            `}</style>
        </Box>
    )
}
